// Account Deletion Service
import { 
    collection, 
    query, 
    where, 
    getDocs, 
    deleteDoc, 
    doc, 
    writeBatch
} from 'firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';
import { deleteUser } from 'firebase/auth';
import { db, storage } from './config.js';

/**
 * Permanently delete a user account and all associated data
 * @param {User} user - Firebase User object
 * @returns {Promise<void>}
 */
export async function deleteUserAccount(user) {
    const uid = user.uid;

    // 1. CLEANUP SITES OWNED BY USER
    const sitesQuery = query(collection(db, 'sites'), where('userId', '==', uid));
    const sitesSnap = await getDocs(sitesQuery);

    for (const siteDoc of sitesSnap.docs) {
        const siteId = siteDoc.id;

        // A. Delete Assets (and their physical files)
        const assetsSnap = await getDocs(collection(db, 'sites', siteId, 'assets'));
        for (const assetDoc of assetsSnap.docs) {
            const assetData = assetDoc.data();
            if (assetData.path) {
                try {
                    const fileRef = ref(storage, assetData.path);
                    await deleteObject(fileRef);
                } catch (err) {
                    console.warn(`Could not delete storage file: ${assetData.path}`, err);
                }
            }
            await deleteDoc(assetDoc.ref);
        }

        // B. Delete Logs
        const logsSnap = await getDocs(collection(db, 'sites', siteId, 'logs'));
        const logBatch = writeBatch(db);
        logsSnap.forEach(d => logBatch.delete(d.ref));
        await logBatch.commit();

        // C. Delete Folders
        const foldersSnap = await getDocs(collection(db, 'sites', siteId, 'folders'));
        const folderBatch = writeBatch(db);
        foldersSnap.forEach(d => folderBatch.delete(d.ref));
        await folderBatch.commit();

        // D. Delete the Site itself
        await deleteDoc(siteDoc.ref);
    }

    // 2. CLEANUP USER PROFILE & TRANSACTIONS
    const transactionsSnap = await getDocs(collection(db, 'users', uid, 'transactions'));
    const txBatch = writeBatch(db);
    transactionsSnap.forEach(d => txBatch.delete(d.ref));
    await txBatch.commit();

    // Delete profile doc
    await deleteDoc(doc(db, 'users', uid));

    // 3. DELETE AUTH ACCOUNT
    // Note: This requires the user to have signed in recently.
    // If it fails with 'auth/requires-recent-login', the UI must handle re-auth.
    await deleteUser(user);
}
